import { useMutation } from '@apollo/client';
import type {
  InsertAssessmentInput,
  InsertInternalAuditReportMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  GetInternalAuditReportByIdDocument,
  GetInternalAuditReportsDocument,
  InsertInternalAuditReportDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertInternalAuditReportTRPC } from './useInsertInternalAuditReportTRPC';

export const useInsertInternalAuditReport = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertInternalAuditReportGraphQL, graphqlState] = useMutation(
    InsertInternalAuditReportDocument,
    {
      update: (cache) => {
        evictField(cache, 'internal_audit_entity');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'internal_audit_report_aggregate');
      },
      refetchQueries: [
        GetInternalAuditReportByIdDocument,
        GetInternalAuditReportsDocument,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertInternalAuditReportTRPC();

  const insertInternalAuditReport = async (
    variables: InsertAssessmentInput
  ): Promise<InsertInternalAuditReportMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertInternalAuditReport({
        ...variables,
        Title: variables.Title ?? '',
        Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
      });
    }

    const result = await insertInternalAuditReportGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert internal audit report');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertInternalAuditReport,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertInternalAuditReport,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
